import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import styles from "./crewWeekGrid.module.css"
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails"
import { useCalendarJournalNoteDates } from "pagesV2/pro/Calendar/hooks/useCalendarSelectors"
import Journal from "pagesV2/pro/Calendar/Modals/Journal"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import useGridVisibility from "pagesV2/pro/Calendar/hooks/useGridVisibility"
import useGridModals from "pagesV2/pro/Calendar/hooks/useGridModals"
import useCrewGridData from "pagesV2/pro/Calendar/hooks/useCrewGridData"
import GridHeaderCell from "./GridHeaderCell"
import GridBodyRows from "./GridBodyRows"

const getJournalPermission = (journalNoteDates, dayStr, canView, canEdit, canCreate) => {
  const hasNote = journalNoteDates?.includes(dayStr)
  return hasNote ? canView || canEdit : canCreate
}

const CrewWeekGrid = ({ calendarDays, events, crewIds, roleIds }) => {
  const { t } = useTranslation()
  const { getVisibleCount, showMore, showLess, maxVisible } = useGridVisibility()
  const modals = useGridModals()
  const journalNoteDates = useCalendarJournalNoteDates()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()
  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const weekStartDate = calendarDays?.[0]?.format("YYYY-MM-DD")
  const weekEndDate = calendarDays?.at(-1)?.format("YYYY-MM-DD")

  const { crewMembers, showUnassigned, eventsByCrewAndDay, unassignedEventsByDay } = useCrewGridData(calendarDays, events, crewIds, roleIds)

  const bodyRows = [
    ...(showUnassigned ? [{ key: "unassigned", label: t("calendar.unassigned"), eventsByDay: unassignedEventsByDay }] : []),
    ...crewMembers.map((crew) => ({ key: String(crew.id), label: crew.name, eventsByDay: eventsByCrewAndDay[crew.id] || {} })),
  ]

  const MIN_ROWS = 4
  const isEmpty = crewMembers.length === 0 && !showUnassigned
  const dummyCount = Math.max(0, MIN_ROWS - bodyRows.length)
  const dummyRows = Array.from({ length: isEmpty ? MIN_ROWS : dummyCount }, (_, i) => (
    <tr key={`dummy-${i}`} className={styles.crewWeekRow}>
      <td className={styles.crewWeekCrewCell} />
      {calendarDays.map((day) => <td key={day.format("YYYY-MM-DD")} className={styles.crewWeekEventCellDummy} />)}
    </tr>
  ))


  return (
    <div className={styles.crewWeekGrid}>
      <table className={styles.crewWeekTable}>
        <thead>
          <tr className={styles.crewWeekHeaderRow}>
            <th className={styles.crewWeekHeaderCell}>{t("calendar.crew_member")}</th>
            {calendarDays.map((day) => {
              const dayStr = day.format("YYYY-MM-DD")
              return (
                <GridHeaderCell
                  key={dayStr}
                  day={day}
                  dayStr={dayStr}
                  journalNoteDates={journalNoteDates}
                  hasJournalPermission={getJournalPermission(journalNoteDates, dayStr, canViewJournal, canEditJournal, canCreateJournal)}
                  isActive={active}
                  onJournalClick={modals.openJournalModal}
                  thClassName={styles.crewWeekHeaderCell}
                />
              )
            })}
          </tr>
        </thead>
        <tbody>
          {isEmpty ? dummyRows : (
            <>
              <GridBodyRows
                rows={bodyRows}
                calendarDays={calendarDays}
                getVisibleCount={getVisibleCount}
                maxVisible={maxVisible}
                showMore={showMore}
                showLess={showLess}
                openBookingModal={modals.openBookingModal}
                rowClassName={styles.crewWeekRow}
                nameCellClassName={styles.crewWeekCrewCell}
                eventCellClassName={styles.crewWeekEventCell}
                eventCellInnerClassName={styles.crewWeekEventCellInner}
              />
              {dummyRows}
            </>
          )}
        </tbody>
      </table>
      {modals.isBookingModalOpen && (
        <BookingDetailsModal
          open={modals.isBookingModalOpen}
          handleOnCancel={modals.closeBookingModal}
          bookingUuid={modals.selectedBookingId}
          onCancelSuccess={modals.handleBookingCancelSuccess}
        />
      )}
      {modals.isJournalModalOpen && (
        <Journal
          open={modals.isJournalModalOpen}
          setOpen={modals.closeJournalModal}
          onCancel={modals.closeJournalModal}
          selectedDate={modals.selectedJournalDate}
          startDate={weekStartDate}
          endDate={weekEndDate}
          range="week"
        />
      )}
    </div>
  )
}

CrewWeekGrid.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
  crewIds: PropTypes.array,
  roleIds: PropTypes.array,
}

export default CrewWeekGrid
